import React, { useEffect, useMemo, useRef, useState } from 'react';
import { t } from 'i18next';
import { useFormikContext } from 'formik';
import { observer } from 'mobx-react-lite';
import { debounce } from 'lodash';
import Skeleton from 'react-loading-skeleton';
import {
  CalcInputWrapper,
  DataInputLabelButton,
  MenuItemsContainer,
  MenuItemsContainerInner,
  MenuItemsSkeletonContainer,
} from '../../widget-calculator.styles';
import FormFieldText from '../../../../../common/form-field-text/form-field-text.component';
import { MenuItems } from '../../../../../common/menu-item/menu-item.styles';
import Button from '../../../../../common/button/button.component';
import {
  FormValues,
  IDataInputNames,
} from '../../widget-calculator-form.component';
import MenuItem from '../../../../../common/menu-item/menu-item.component';
import { useStore } from '../../../../../stores/store';

import Separator from '../../../../../common/separator/separator.component';
import useGeolocation from '../../../../../hooks/use-geolocation.hook';
import { Modals } from '../../../../../constants/modals';
import { useModal } from '../../../../../common/modal/use-modal.hook';
import { toLatLng } from '../../../../../common/map/map.helpers';
import { getRouteFieldLabel } from '../../helpers';
import {
  GeoapifyService,
  GeopifyLocationDetails,
} from '../../../../../utils/geoapify/geoapify';
import { scrollInputToRight } from '../../../../../utils/forms/forms.utils';
import { CountryDetails } from '../../../../../models/shipmentCountryInfo';

interface Props {
  onFocus: () => void;
  onBlur: (e?: React.FocusEvent<HTMLInputElement>) => void;
  onDataInputLabelButtonClick: (inputName: IDataInputNames) => void;
  showInput: boolean;
  isDeparture: boolean;
  toggleInput: (arg: boolean) => void;
  getSearchOptions: (
    filter?: string
  ) => Promise<GeopifyLocationDetails[] | undefined>;
  label: string;
  name: IDataInputNames;
  hiddenInputName: IDataInputNames;
  onSelect?: () => void;
  disabled?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isFrameMode?: boolean;
  isDomestic?: boolean;
}

const InputZipFormField = ({
  onFocus,
  onBlur,
  onDataInputLabelButtonClick,
  showInput,
  isDeparture,
  toggleInput,
  getSearchOptions,
  onSelect,
  onChange,
  label,
  name,
  hiddenInputName,
  disabled,
  isFrameMode,
  isDomestic,
}: Props) => {
  const {
    widgetCalculatorStore: { findShipmentCountry },
    shipmentCountriesStore: { receiverCountries },
    commonStore: { toastError },
  } = useStore();

  const [searchOptions, setSearchOptions] = useState<GeopifyLocationDetails[]>(
    []
  );
  const [showSearchOptions, setShowSearchOptions] = useState(false);
  const [isBlurRequested, setIsBlurRequested] = useState(false);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [isReverseGeocodingLoading, setIsReverseGeocodingLoading] =
    useState(false);
  const [isLabelClicked, setIsLabelClicked] = useState(false);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const dropdownRef = useRef<HTMLInputElement | null>(null);

  const { setFieldValue, setFieldTouched, errors, values, getFieldMeta } =
    useFormikContext<FormValues>();

  const fieldMeta = getFieldMeta<string>(name);
  const fieldValue = fieldMeta.value;

  const handleSelect = (option: GeopifyLocationDetails | null) => {
    if (!option || !option.countryCode) {
      setShowSearchOptions(false);
      onSelect?.();
      return;
    }

    const { countryCode, postcode: zipCode, city, formatted } = option;

    let countryInfo: CountryDetails | null | undefined;

    if (isDeparture || isDomestic) {
      countryInfo = findShipmentCountry(countryCode, undefined);
    } else {
      countryInfo = findShipmentCountry(countryCode, receiverCountries);
    }
    const displayValue = [zipCode, city].filter(Boolean).join(', ');
    setFieldValue(name, displayValue || formatted);

    if (countryInfo) {
      const newCountryInfo: CountryDetails = {
        ...countryInfo,
        code: countryCode,
        zipCode,
        city,
      };

      setFieldValue(hiddenInputName, newCountryInfo);
    }

    setFieldValue('validationContext', {
      ...values.validationContext,
      [isDeparture ? 'departureNotSupported' : 'destinationNotSupported']:
        !countryInfo,
      [isDeparture
        ? 'predictionsNotFoundDeparture'
        : 'predictionsNotFoundDestination']: false,
      [isDeparture
        ? 'departureSelectionConfirmed'
        : 'destinationSelectionConfirmed']: true,
    });

    setShowSearchOptions(false);
    onSelect?.();
  };

  const handleSearchOptionSelected = async (option: GeopifyLocationDetails) => {
    // eslint-disable-next-line prefer-const

    let locationDetails: GeopifyLocationDetails | null = option;

    // Try search using geocode
    if (!option.postcode && option.placeId) {
      setIsReverseGeocodingLoading(true);
      const geoapifyService = new GeoapifyService();
      const reverseGeocodeDetails = await geoapifyService.getDetails({
        lat: option.lat,
        lng: option.lon,
      });

      setIsReverseGeocodingLoading(false);
      locationDetails = reverseGeocodeDetails;
    }

    handleSelect(locationDetails);
  };

  async function handleLocationSuccess(position: GeolocationPosition) {
    const geoapifyService = new GeoapifyService();
    const placeDetails = await geoapifyService.getDetails(
      toLatLng(position.coords)
    );

    handleSelect(placeDetails);
  }

  function handleLocationError(error: GeolocationPositionError) {
    toastError(
      error?.code === 1 ? t('service_needs_location') : `${error?.message}`
    );
  }

  const { openModal, closeModal } = useModal();

  const {
    isGeolocationAvailable,
    isLocationLoading,
    tryGetLocation,
    geoPermissionState,
  } = useGeolocation({
    onLocationSuccess: handleLocationSuccess,
    onLocationError: handleLocationError,
  });

  function handleGeoPermissionChange(state: PermissionState) {
    switch (state) {
      case 'granted':
        tryGetLocation();
        break;
      case 'prompt':
        openModal({
          id: Modals.ALLOW_LOCATION_ACCESS,
          name: Modals.ALLOW_LOCATION_ACCESS,
          props: {
            onApprove: () => {
              closeModal(Modals.ALLOW_LOCATION_ACCESS);
              toggleInput(true);
              tryGetLocation();
            },
            onClose: () => {
              closeModal(Modals.ALLOW_LOCATION_ACCESS);
            },
          },
        });
        break;
      case 'denied':
        toastError(t('the_location_service_is_off'));
        break;
      default:
        break;
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdownRef, toggleInput]);

  // Required to resume postponed blur after dropdown is closed and value is updated.
  useEffect(() => {
    if (isBlurRequested && !showSearchOptions) {
      onBlur();
      setIsBlurRequested(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBlurRequested, showSearchOptions]);

  useEffect(() => {
    if (!showInput) {
      inputRef?.current?.blur();
      return;
    }

    if (showInput && isLabelClicked) {
      inputRef?.current?.focus();
      setIsLabelClicked(false);
    }
  }, [isLabelClicked, showInput, searchOptions]);

  useEffect(() => {
    if (!fieldValue) {
      setSearchOptions([]);
    }
  }, [fieldValue]);

  function handleClickOutside(event: MouseEvent) {
    // For destination case when haven't searched yet and dropdown is not shown
    if (!dropdownRef.current) {
      setShowSearchOptions(false);
      if (!inputRef.current?.value) {
        toggleInput(false);
      }
    }

    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node) &&
      inputRef.current &&
      !inputRef.current.contains(event.target as Node)
    ) {
      setShowSearchOptions(false);
      if (!inputRef.current?.value) {
        toggleInput(false);
      }
    }
  }

  const showNearMe = !isFrameMode && isDeparture;

  const updateSearchOptionsDebounced = useMemo(
    () =>
      debounce(async (filter?: string) => {
        setIsSearchLoading(true);
        const predictions = await getSearchOptions(filter);
        const isFound = !!predictions && predictions.length > 0;

        if (isFound) {
          setSearchOptions(predictions ?? []);
          setShowSearchOptions(true);

          setFieldValue('validationContext', {
            ...values.validationContext,
            [isDeparture
              ? 'predictionsNotFoundDeparture'
              : 'predictionsNotFoundDestination']: false,
          });
        } else {
          setSearchOptions([]);
          setShowSearchOptions(showNearMe || false);

          setFieldValue('validationContext', {
            ...values.validationContext,
            [isDeparture
              ? 'predictionsNotFoundDeparture'
              : 'predictionsNotFoundDestination']: true,
          });
        }
        setIsSearchLoading(false);
      }, 750),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      getSearchOptions,
      setFieldValue,
      showNearMe,
      // values.validationContext,
      // isDeparture,
    ]
  );

  const modifyListOfDisplayedOptions = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = e.target;
    setFieldTouched(name, true);
    setFieldTouched(hiddenInputName, true);
    // User typed, require selection confirmation again
    setFieldValue('validationContext', {
      ...values.validationContext,
      [isDeparture
        ? 'departureSelectionConfirmed'
        : 'destinationSelectionConfirmed']: false,
    });

    if (value) {
      updateSearchOptionsDebounced(value);
    } else {
      setSearchOptions([]);
    }
  };

  const searchOptionsAvailable = !!searchOptions && searchOptions.length > 0;

  let showDropdown = showSearchOptions;

  if (!showNearMe) {
    showDropdown &&= searchOptionsAvailable || isSearchLoading;
  }

  return (
    <CalcInputWrapper>
      <DataInputLabelButton
        $isInvalid={!!errors[name] || !!errors[hiddenInputName]}
        disabled={disabled}
        onClick={() => {
          onDataInputLabelButtonClick(name);

          // stupid workaround for mobile Safari to focus field after click
          // and not focus when its value was autofilled with user address
          setIsLabelClicked(true);
        }}>
        {t(label)}
      </DataInputLabelButton>

      <FormFieldText
        disabled={disabled}
        $mb='0.5rem'
        $borderNone
        ref={inputRef}
        validationMethod='always'
        label={t(label)}
        name={name}
        showSkeleton={isReverseGeocodingLoading}
        skeletonClassName='calc-input-skeleton'
        isHidden={!showInput}
        isLabelHidden
        onChange={(e) => {
          onChange && onChange(e);
          modifyListOfDisplayedOptions(e);
        }}
        type='text'
        placeholder={getRouteFieldLabel(values.departure_country?.code)}
        showValidationError={false}
        onFocus={() => {
          onFocus();
          setShowSearchOptions(true);
          scrollInputToRight(inputRef);
        }}
        onBlur={(e) => {
          // ignore field blur while dropdown is opened to prevent unnecessary validation.
          if (showDropdown) {
            setIsBlurRequested(true);
            return;
          }
          onBlur(e);
        }}
      />
      {showDropdown && (
        <MenuItemsContainer
          ref={dropdownRef}
          onBlur={() => {
            setShowSearchOptions(false);
            if (!inputRef.current?.value) {
              toggleInput(false);
            }
          }}>
          <MenuItemsContainerInner>
            {showNearMe && (
              <Button
                onClick={() => {
                  setFieldTouched(name, true);
                  setFieldTouched(hiddenInputName, true);
                  if (isGeolocationAvailable) {
                    handleGeoPermissionChange(geoPermissionState);
                  } else {
                    toastError(t('geolocation_is_not_supported'));
                  }
                }}
                appearance='text'
                size='small'
                className='nearMeButton'
                fullWidth={false}
                disabled={isLocationLoading}
                icon={{ glyph: 'location-cross', position: 'left' }}>
                {t('near_me')}
              </Button>
            )}
            {showNearMe && (searchOptionsAvailable || isSearchLoading) && (
              <Separator
                mt='0'
                mr='.8rem'
                ml='.8rem'
              />
            )}
            {!isSearchLoading && (
              <MenuItems>
                {searchOptions?.map((option) => (
                  <MenuItem
                    key={option.placeId}
                    useFlagEmojiFont={false}
                    onClick={() => handleSearchOptionSelected(option)}>
                    {option.formatted}
                  </MenuItem>
                ))}
              </MenuItems>
            )}
            {isSearchLoading && (
              <MenuItemsSkeletonContainer>
                <Skeleton
                  count={2}
                  height='2.8rem'
                  className='calc-menu-item-skeleton'
                  borderRadius={6}
                />
              </MenuItemsSkeletonContainer>
            )}
          </MenuItemsContainerInner>
        </MenuItemsContainer>
      )}
    </CalcInputWrapper>
  );
};

export default observer(InputZipFormField);
